import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { OpenInNewTab } from "../datagrid/OpenInNewTab";
import { Box, Tooltip, Typography } from "@mui/material";
import {
	getLabelByServiceId,
	getPeriodicityLabel,
	lastestBookedValidationColor,
} from "../../screens/treatments/treatment-prescription/utils";
import { TreatmentProvidedAppointment } from "../../api/useTreatmentsProvidedAppointments";

export const columns: GridColDef[] = [
	{
		field: "new_tab",
		headerName: "Nova tab",
		width: 80,
		renderCell: (params: GridCellParams) => <OpenInNewTab params={params} entity={"treatments"} />,
	},
	{
		field: "client_id",
		headerName: "Cliente",
		width: 80,
	},
	{
		field: "client_name",
		headerName: "Nome",
		width: 200,
	},
	{
		field: "client_owner_name",
		headerName: "Gestor de Caso",
		width: 200,
	},
	{
		field: "last_session_done",
		headerName: "Última Sessão",
		width: 250,
		renderHeader: () => (
			<Typography variant="body2"  sx={{ pl: 2 }}>
				Última Sessão
			</Typography>
		),
		renderCell: (params: GridCellParams) => <LastSessions params={params} />,
	},
	{
		field: "next_session_booked",
		headerName: "Próxima Sessão",
		width: 300,
		renderHeader: () => (
			<Typography variant="body2"  sx={{ pl: 2 }}>
				Próxima Sessão
			</Typography>
		),
		renderCell: (params: GridCellParams) => <NextSessions params={params} />,
	},

	{
		field: "previous_period_fulfillment",
		headerName: "Agendamento C. Período Anterior",
		width: 320,
		renderHeader: () => (
			<Typography variant="body2"  sx={{ pl: 2 }}>
				Agendamento C. Período Anterior
			</Typography>
		),
		renderCell: (params: GridCellParams) => <PreviousPeriodFulfillment params={params} />,
	},
	{
		field: "appointment_fulfillment",
		headerName: "Agendamento Cumprido Atual",
		width: 300,
		renderHeader: () => (
			<Typography variant="body2"  sx={{ pl: 2 }}>
				Agendamento Cumprido Atual
			</Typography>
		),
		renderCell: (params: GridCellParams) => <Fulfillment params={params} />,
	},
	{
		field: "sessions_done_vs_expected",
		headerName: "Realizadas / Esperadas (Geral)",
		width: 250,
		renderHeader: () => (
			<Typography variant="body2"  sx={{ pl: 2 }}>
				Realizadas / Esperadas (Geral)
			</Typography>
		),
		renderCell: (params: GridCellParams) => <DoneVsExpected params={params} />,
	},
	{
		field: "attendance_rate",
		headerName: "Taxa de Presença",
		width: 130,
		renderHeader: () => (
			<Tooltip title="Percentagem de sessões 'Realizada' em relação às 'Cancelada' e 'Não Compareceu'" arrow placement="top">
				<Typography variant="body2">
					Taxa de Presença
				</Typography>
			</Tooltip>
		),
		renderCell: (params: GridCellParams) => {
			const value = params.value as number | null;
			const displayValue = value !== null ? `${value}%` : "N/A";
			return (
				<Typography variant="body2">
					{displayValue}
				</Typography>
			);
		},
	},
];

const DoneVsExpected = ({ params }: { params: GridCellParams }) => {
	return (
		<CustomCell
			params={params}
			field="sessions_done_vs_expected"
			renderDisplay={(item) => `: ${item.done} / ${item.expected}`}
			color={(item) => ((item.done || item.done === 0) && item.done < item.expected ? "red" : "default")}
		/>
	);
};

const Fulfillment = ({ params }: { params: GridCellParams }) => {
	return (
		<CustomCell
			params={params}
			field="appointment_fulfillment"
			renderDisplay={(item) =>
				`: ${item.booked_sessions} / ${item.prescribed_quantity} ${
					getPeriodicityLabel(item.periodicity)?.frequency
				}`
			}
			color={(item) => (item.booked_sessions < item.prescribed_quantity ? "red" : "default")}
		/>
	);
};

const PreviousPeriodFulfillment = ({ params }: { params: GridCellParams }) => {
	return (
		<CustomCell
			params={params}
			field="previous_period_fulfillment"
			renderDisplay={(item) => {
				if (item.periodicity === "not_applicable" || item.started_in_current_period) {
					return ": Não Aplicável";
				}
				return `: ${item.booked_sessions} / ${item.prescribed_quantity} ${
					getPeriodicityLabel(item.periodicity, true)?.frequency
				}`;
			}}
			color={(item) => {
				if (item.periodicity === "not_applicable" || item.started_in_current_period) {
					return "default";
				}
				return item.booked_sessions < item.prescribed_quantity ? "red" : "default";
			}}
		/>
	);
};

const NextSessions = ({ params }: { params: GridCellParams<TreatmentProvidedAppointment> }) => {
	return (
		<CustomCell
			params={params}
			field="next_session_booked"
			renderDisplay={(item) => `: ${item.next_booked || "Não Agendada"}`}
			color={(item) => lastestBookedValidationColor(item.next_booked)}
			filter={(id, _) => params.row.appointment_fulfillment.hasOwnProperty(Number(id))}
		/>
	);
};
const LastSessions = ({ params }: { params: GridCellParams }) => {
	return (
		<CustomCell
			params={params}
			field="last_session_done"
			renderDisplay={(item) => `: ${item.last_done}`}
			filter={(id, _) => params.row.appointment_fulfillment.hasOwnProperty(Number(id))}
		/>
	);
};

type CustomCellProps<K extends keyof TreatmentProvidedAppointment> = {
	params: GridCellParams<TreatmentProvidedAppointment>;
	field: K;
	renderDisplay: (item: TreatmentProvidedAppointment[K] extends Record<string, infer V> ? V : never) => string;
	filter?: (id: string, item: TreatmentProvidedAppointment[K] extends Record<string, infer V> ? V : never) => boolean;
	color?: (item: TreatmentProvidedAppointment[K] extends Record<string, infer V> ? V : never) => string;
};

const CustomCell = <K extends keyof TreatmentProvidedAppointment>({
	params,
	field,
	renderDisplay,
	filter,
	color,
}: CustomCellProps<K>) => {
	const data = params.row?.[field] as Record<string, any>;

	if (!data) return null;
	return (
		<Box
			key={params.id}
			display="flex"
			flexDirection="column"
			justifyContent="center"
			minHeight="40px"
			px={4}
			borderRadius={10}
			sx={{ backgroundColor: "grey.100" }}
		>
			{Object.entries(data)
				.filter(([id, item]) => (filter ? filter(id, item) : true))
				.map(([id, item]) => {
					const serviceName = getLabelByServiceId(Number(id));

					const formattedValue = params.formattedValue as Record<string, { periodicity: string }>;
					const hideDetails = formattedValue?.[id]?.periodicity === "not_applicable" && field === "appointment_fulfillment";
					return (
						<Box key={id}>
							<Typography
								variant="body2"
								color={hideDetails ? "default" : color ? color(item) : "default"}
							>
								<Box component="span" fontWeight="bold" color={"text.primary"}>
									{serviceName}
								</Box>
								{hideDetails ? ": Não Periódico" : renderDisplay(item)}
							</Typography>
						</Box>
					);
				})}
		</Box>
	);
};
